import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Pause, Play } from 'lucide-react';
import { motion } from 'framer-motion';

const COLS = 10;
const ROWS = 20;
const CELL_SIZE = 30;

const SHAPES = [
  [[1, 1, 1, 1]], // I
  [[1, 1], [1, 1]], // O
  [[0, 1, 0], [1, 1, 1]], // T
  [[1, 1, 0], [0, 1, 1]], // S
  [[0, 1, 1], [1, 1, 0]], // Z
  [[1, 0, 0], [1, 1, 1]], // L
  [[0, 0, 1], [1, 1, 1]]  // J
];

const COLORS = ['#FF6B9D', '#81B29A', '#FFD6A5', '#A8DADC', '#DDA0DD', '#B9FBC0', '#FFE66D'];

const MindfulBlocks = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const gridRef = useRef(Array(ROWS).fill().map(() => Array(COLS).fill(0)));
  const currentPieceRef = useRef(null);
  const nextPieceRef = useRef(null);
  const animationRef = useRef(null);
  const lastDropRef = useRef(Date.now());
  const gameOverRef = useRef(false);
  const isPausedRef = useRef(false);
  const scoreRef = useRef(0);
  const levelRef = useRef(1);
  const [nextPiece, setNextPiece] = useState(null);

  useEffect(() => {
    gameOverRef.current = gameOver;
  }, [gameOver]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    initGame();
    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameOverRef.current || isPausedRef.current) return;

      if (e.key === 'ArrowLeft' || e.key === 'a') movePiece(-1, 0);
      if (e.key === 'ArrowRight' || e.key === 'd') movePiece(1, 0);
      if (e.key === 'ArrowDown' || e.key === 's') movePiece(0, 1);
      if (e.key === 'ArrowUp' || e.key === 'w' || e.key === ' ') rotatePiece();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const initGame = () => {
    gridRef.current = Array(ROWS).fill().map(() => Array(COLS).fill(0));
    spawnPiece();
    nextPieceRef.current = createPiece();
    setNextPiece(nextPieceRef.current);
  };

  const createPiece = () => {
    const shapeIndex = Math.floor(Math.random() * SHAPES.length);
    return {
      shape: SHAPES[shapeIndex],
      color: COLORS[shapeIndex],
      x: Math.floor(COLS / 2) - 1,
      y: 0
    };
  };

  const spawnPiece = () => {
    if (nextPieceRef.current) {
      currentPieceRef.current = nextPieceRef.current;
      nextPieceRef.current = createPiece();
    } else {
      currentPieceRef.current = createPiece();
      nextPieceRef.current = createPiece();
    }
    setNextPiece(nextPieceRef.current);

    if (checkCollision(currentPieceRef.current, 0, 0)) {
      setGameOver(true);
    }
  };

  const checkCollision = (piece, offsetX, offsetY) => {
    for (let y = 0; y < piece.shape.length; y++) {
      for (let x = 0; x < piece.shape[y].length; x++) {
        if (piece.shape[y][x]) {
          const newX = piece.x + x + offsetX;
          const newY = piece.y + y + offsetY;
          
          if (newX < 0 || newX >= COLS || newY >= ROWS) return true;
          if (newY >= 0 && gridRef.current[newY][newX]) return true;
        }
      }
    }
    return false;
  };

  const movePiece = (dx, dy) => {
    if (!currentPieceRef.current || checkCollision(currentPieceRef.current, dx, dy)) {
      if (dy > 0) {
        lockPiece();
      }
      return;
    }
    currentPieceRef.current.x += dx;
    currentPieceRef.current.y += dy;
  };

  const rotatePiece = () => {
    if (!currentPieceRef.current) return;
    
    const rotated = currentPieceRef.current.shape[0].map((_, i) =>
      currentPieceRef.current.shape.map(row => row[i]).reverse()
    );
    
    const originalShape = currentPieceRef.current.shape;
    currentPieceRef.current.shape = rotated;
    
    if (checkCollision(currentPieceRef.current, 0, 0)) {
      currentPieceRef.current.shape = originalShape;
    }
  };

  const lockPiece = () => {
    const piece = currentPieceRef.current;
    
    for (let y = 0; y < piece.shape.length; y++) {
      for (let x = 0; x < piece.shape[y].length; x++) {
        if (piece.shape[y][x]) {
          const gridY = piece.y + y;
          const gridX = piece.x + x;
          if (gridY >= 0) {
            gridRef.current[gridY][gridX] = piece.color;
          }
        }
      }
    }

    clearLines();
    spawnPiece();
  };

  const clearLines = () => {
    let linesCleared = 0;
    
    for (let y = ROWS - 1; y >= 0; y--) {
      if (gridRef.current[y].every(cell => cell !== 0)) {
        gridRef.current.splice(y, 1);
        gridRef.current.unshift(Array(COLS).fill(0));
        linesCleared++;
        y++;
      }
    }

    if (linesCleared > 0) {
      const newScore = scoreRef.current + linesCleared * 100 * levelRef.current;
      const newLevel = Math.floor(newScore / 1000) + 1;
      scoreRef.current = newScore;
      levelRef.current = newLevel;
      setScore(newScore);
      setLevel(newLevel);
    }
  };

  const animate = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = COLS * CELL_SIZE;
    canvas.height = ROWS * CELL_SIZE;

    // Background
    ctx.fillStyle = '#F5F5F5';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Grid lines
    ctx.strokeStyle = '#E0E0E0';
    ctx.lineWidth = 1;
    for (let x = 0; x <= COLS; x++) {
      ctx.beginPath();
      ctx.moveTo(x * CELL_SIZE, 0);
      ctx.lineTo(x * CELL_SIZE, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y <= ROWS; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * CELL_SIZE);
      ctx.lineTo(canvas.width, y * CELL_SIZE);
      ctx.stroke();
    }

    // Draw locked blocks
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (gridRef.current[y][x]) {
          ctx.fillStyle = gridRef.current[y][x];
          ctx.fillRect(x * CELL_SIZE + 2, y * CELL_SIZE + 2, CELL_SIZE - 4, CELL_SIZE - 4);
        }
      }
    }

    // Draw current piece
    if (currentPieceRef.current) {
      ctx.fillStyle = currentPieceRef.current.color;
      for (let y = 0; y < currentPieceRef.current.shape.length; y++) {
        for (let x = 0; x < currentPieceRef.current.shape[y].length; x++) {
          if (currentPieceRef.current.shape[y][x]) {
            const px = (currentPieceRef.current.x + x) * CELL_SIZE;
            const py = (currentPieceRef.current.y + y) * CELL_SIZE;
            ctx.fillRect(px + 2, py + 2, CELL_SIZE - 4, CELL_SIZE - 4);
          }
        }
      }
    }

    // Auto drop
    if (!gameOverRef.current && !isPausedRef.current) {
      const now = Date.now();
      const dropInterval = Math.max(100, 1000 - (levelRef.current - 1) * 100);
      
      if (now - lastDropRef.current > dropInterval) {
        movePiece(0, 1);
        lastDropRef.current = now;
      }
    }

    animationRef.current = requestAnimationFrame(animate);
  };

  const reset = () => {
    scoreRef.current = 0;
    levelRef.current = 1;
    gameOverRef.current = false;
    isPausedRef.current = false;
    setScore(0);
    setLevel(1);
    setGameOver(false);
    setIsPaused(false);
    initGame();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8E4F3] to-[#D4CDD4] relative overflow-hidden">
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#2D3748] mb-2">Mindful Blocks</h1>
            <div className="flex gap-6 justify-center text-lg">
              <p className="text-[#4A5568]">Score: <span className="font-bold text-[#81B29A]">{score}</span></p>
              <p className="text-[#4A5568]">Level: <span className="font-bold text-[#81B29A]">{level}</span></p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              data-testid="toggle-pause"
              onClick={() => setIsPaused(!isPaused)}
              className="bg-[#81B29A] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2"
            >
              {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
            </button>
            <button
              data-testid="reset-game"
              onClick={reset}
              className="bg-white border-2 border-[#81B29A] text-[#81B29A] px-6 py-3 rounded-full font-semibold hover:bg-[#81B29A]/10 transition-all flex items-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </header>

        {gameOver && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-red-500 mb-8 text-center"
          >
            <div className="text-6xl mb-4">🧩</div>
            <h2 className="text-3xl font-bold text-red-600 mb-2">Game Over</h2>
            <p className="text-xl text-[#4A5568] mb-2">Final Score: {score}</p>
            <p className="text-lg text-[#4A5568]">Level Reached: {level}</p>
            <button
              data-testid="play-again"
              onClick={reset}
              className="mt-6 bg-[#81B29A] text-white px-8 py-3 rounded-full font-bold hover:-translate-y-1 transition-all shadow-lg"
            >
              Play Again
            </button>
          </motion.div>
        )}

        <div className="flex justify-center gap-8">
          <div className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-100">
            <canvas ref={canvasRef} className="rounded-2xl" />
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
              <h3 className="text-xl font-bold text-[#2D3748] mb-4">Next Block</h3>
              <div className="w-32 h-32 bg-slate-50 rounded-xl flex items-center justify-center">
                {nextPiece ? (
                  <div
                    className="grid gap-1"
                    style={{ gridTemplateColumns: `repeat(${nextPiece.shape[0].length}, 1fr)` }}
                  >
                    {nextPiece.shape.map((row, y) =>
                      row.map((cell, x) => (
                        <div
                          key={`${y}-${x}`}
                          className="w-4 h-4 rounded-sm"
                          style={{ backgroundColor: cell ? nextPiece.color : 'transparent' }}
                        />
                      ))
                    )}
                  </div>
                ) : (
                  <div className="text-4xl">🧩</div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
              <h3 className="text-lg font-bold text-[#2D3748] mb-3">Controls</h3>
              <div className="space-y-2 text-sm text-[#4A5568]">
                <p><span className="font-semibold">← → or A D:</span> Move</p>
                <p><span className="font-semibold">↓ or S:</span> Drop faster</p>
                <p><span className="font-semibold">↑ or W/Space:</span> Rotate</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-2xl p-6 text-center shadow-lg">
          <p className="text-[#4A5568] text-lg">
            Stack blocks mindfully. Clear lines to score points. The game speeds up as you level up!
          </p>
        </div>

        {/* On-screen touch controls */}
        <div className="mt-8 flex justify-center">
          <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-100">
            <div className="flex items-center gap-6">
              {/* Left/Right/Down controls */}
              <div className="grid grid-cols-3 gap-4">
                <div></div>
                <button
                  data-testid="rotate-block"
                  onPointerDown={rotatePiece}
                  disabled={gameOver || isPaused}
                  className="w-20 h-20 bg-gradient-to-br from-[#DDA0DD] to-[#C78EC7] rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg hover:-translate-y-1 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ touchAction: 'manipulation' }}
                >
                  ↻
                </button>
                <div></div>
                <button
                  data-testid="move-left-block"
                  onPointerDown={() => movePiece(-1, 0)}
                  disabled={gameOver || isPaused}
                  className="w-20 h-20 bg-gradient-to-br from-[#81B29A] to-[#6A9B84] rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg hover:-translate-y-1 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ touchAction: 'manipulation' }}
                >
                  ←
                </button>
                <button
                  data-testid="drop-block"
                  onPointerDown={() => movePiece(0, 1)}
                  disabled={gameOver || isPaused}
                  className="w-20 h-20 bg-gradient-to-br from-[#FFD6A5] to-[#FFC280] rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg hover:-translate-y-1 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ touchAction: 'manipulation' }}
                >
                  ↓
                </button>
                <button
                  data-testid="move-right-block"
                  onPointerDown={() => movePiece(1, 0)}
                  disabled={gameOver || isPaused}
                  className="w-20 h-20 bg-gradient-to-br from-[#81B29A] to-[#6A9B84] rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg hover:-translate-y-1 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ touchAction: 'manipulation' }}
                >
                  →
                </button>
              </div>
            </div>
            <p className="text-[#4A5568] text-sm mt-4 text-center">Touch controls or use keyboard (arrows/WASD)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MindfulBlocks;